// Note for Angular2+ migration:
// The AngularJS interceptors are not compatible with Angular2+ and Angular2+ are not applicable on AngularJS
// services. Therefore, this is needed until we're done converting all of our services over to Angular
// Reference: https://auth0.com/blog/upgrade-your-angularjs-authentication-service/

import * as angular from "angular";
import { CredentialsManager } from "../shared/credentials-manager";
import { InterceptorUtils } from "../helper/interceptor.helper";
import * as Sentry from "@sentry/browser";
import { sentryClient } from "./sentry-client";

angular
    .module("requestInterceptor", ["credentialsManager", "vcHelper"])
    .factory(
        "httpRequestInterceptor",
        function (
            __env: any,
            $q: any,
            __time_zone: any,
            $injector: any,
            __app_type: string,
            InterceptorUtils: InterceptorUtils,
            CredentialsManager: CredentialsManager
        ) {
            let reauthLoopReported = false;

            const reportReauthLoop = (config: any): void => {
                const data = { url: config?.url, method: config?.method };
                if (reauthLoopReported) {
                    sentryClient.addBreadcrumb({
                        category: "auth",
                        message: "Reauth loop detected",
                        level: Sentry.Severity.Warning,
                        data,
                    });
                    return;
                }
                reauthLoopReported = true;
                sentryClient.captureMessage("Reauth loop detected", {
                    level: Sentry.Severity.Warning,
                    extra: data,
                });
            };

            return {
                request: (config: any): any => {
                    // A reauth retry sends the same config through again.
                    config.__baseUrl ??= config.url;
                    config.url = config.__baseUrl;

                    if (angular.isDefined(config.method)) {
                        if (config.method == "POST") {
                            config.data = InterceptorUtils.lookForIgnoreList(
                                config.data
                            );
                        } else if (
                            angular.isDefined(config.method) &&
                            config.method == "GET"
                        ) {
                            config.params = InterceptorUtils.lookForIgnoreList(
                                config.params
                            );
                        }
                    }

                    if (
                        config.url.indexOf(__env.apiUrl()) == 0 ||
                        config.url.indexOf("/api") == 0 ||
                        config.url.indexOf(__env.apiUrl("v2")) == 0 ||
                        config.url.indexOf("/v2") == 0
                    ) {
                        if (config.url.indexOf("?") > -1) {
                            config.url +=
                                "&campaignid=" + CredentialsManager.campaignid;
                        } else {
                            config.url +=
                                "?campaignid=" + CredentialsManager.campaignid;
                        }

                        if (CredentialsManager.oid) {
                            if (
                                CredentialsManager.leaderView &&
                                config.url.indexOf("organisation") == -1 &&
                                config.url.indexOf("api/campaign") == -1
                            ) {
                                config.url = config.url.replace(
                                    "/api",
                                    "/api/organisation"
                                );

                                if (config.url.indexOf("v2/survey") != -1) {
                                    config.url = config.url.replace(
                                        "/v2/survey",
                                        "/api/organisation/survey"
                                    );
                                }
                            }
                            config.url += "&oid=" + CredentialsManager.oid;

                            config.url +=
                                "&view=" +
                                (__app_type === "LEADER"
                                    ? "leader"
                                    : "campaign");
                        }
                        config.url +=
                            "&timezone=" + encodeURIComponent(__time_zone);
                        config.url += "&u=" + CredentialsManager.uid;
                        config.headers["Authorization"] =
                            "Bearer " + CredentialsManager.getAccessToken();
                    }
                    return config;
                },

                responseError: (response: any): Promise<any> => {
                    if (response.status == 401) {
                        const $http = $injector.get("$http");
                        const defer = $q.defer();

                        if (response.config?.__reauthRetried) {
                            reportReauthLoop(response.config);
                            defer.reject(response);
                            return defer.promise;
                        }

                        CredentialsManager.reauth().then(
                            () => {
                                //try method again with new credentials
                                if (response.config) {
                                    response.config.__reauthRetried = true;
                                }
                                $http(response.config).then(
                                    (retryResponse: object) =>
                                        defer.resolve(retryResponse),
                                    (error: object) => defer.reject(error)
                                );
                            },
                            (error: object) => defer.reject(error)
                        );
                        return defer.promise;
                    } else {
                        if (response.status >= 500) {
                            sentryClient.addBreadcrumb({
                                category: "http",
                                message: "Server error",
                                level: Sentry.Severity.Error,
                                data: {
                                    url: response.config?.url,
                                    method: response.config?.method,
                                    status: response.status,
                                    statusText: response.statusText,
                                    responseData: response.data,
                                },
                            });
                        } else {
                            sentryClient.addBreadcrumb({
                                category: "http",
                                message: `${response.config?.method} ${response.config?.url}`,
                                level: Sentry.Severity.Warning,
                                data: {
                                    status: response.status,
                                    responseData: response.data,
                                },
                            });
                        }
                        console.log(response);
                        return $q.reject(response);
                    }
                },
            };
        }
    );
